import React, { memo, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Zap,
  Send,
  Lock,
  Unlock,
  ArrowRight,
  Users,
  CheckCircle2,
  AlertCircle,
  Crown,
  Sparkles,
  Camera,
  BookOpen,
  Bell,
  ShieldCheck,
} from 'lucide-react';

export interface ActionNodeData {
  actionType?: 'whatsapp_lead' | 'quick_sale' | 'stock_check' | 'webhook' | 'hotel_checkin';
  targetDestination?: string;
  autoExecute?: boolean;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const ActionNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as ActionNodeData;
  const [saveIndicator, setSaveIndicator] = React.useState<string | null>(null);
  const debounceTimerRef = React.useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, tenant, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const persistNodeDataDirectly = (field: keyof ActionNodeData, value: any, allUpdates?: Partial<ActionNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const currentNodes = getNodes ? getNodes() : [];
        const liveNode = currentNodes.find((n) => n.id === id);
        const currentData = (liveNode?.data || nodeData || {}) as ActionNodeData;
        const payloadData: any = {
          ...currentData,
          [field]: value,
          ...(allUpdates || {})
        };
        // Eliminate recursive widgetData nesting
        if (payloadData.widgetData) {
          delete payloadData.widgetData;
        }

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }
        const effectiveTenantId = isTenantView && tenant?.id ? String(tenant.id) : undefined;
        if (effectiveTenantId) {
          headers['x-tenant-id'] = effectiveTenantId;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'ACTION_NODE',
            data: payloadData,
            widgetData: payloadData,
            tenantId: effectiveTenantId,
          }),
        });

        if (res.ok) {
          setSaveIndicator('SAVED');
          setTimeout(() => setSaveIndicator(null), 2000);
        }
      } catch (err) {
        console.warn('[ActionNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof ActionNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              [field]: value,
            },
          };
        }
        return node;
      })
    );
    persistNodeDataDirectly(field, value);
  };

  const isWired = useMemo(() => {
    return isNodeWiredToLunaCore(id, getEdges(), getNodes());
  }, [id, getEdges, getNodes]);

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';

  const isPhoneDestination = nodeData.actionType === 'whatsapp_lead' || Boolean(nodeData.targetDestination && !nodeData.targetDestination.startsWith('http'));

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[400px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#140C22]/98 via-[#0A0612]/98 to-[#05030A]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-purple-400 ring-2 ring-purple-400/50 shadow-[0_0_35px_rgba(168,85,247,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-purple-500/40 hover:border-purple-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(168,85,247,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Leads in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('actionNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-950 via-slate-900 to-indigo-950 border-2 border-purple-400 text-purple-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(168,85,247,0.8)] hover:border-purple-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view captured leads and action metrics"
          >
            <Zap size={13} className="text-purple-300 animate-pulse" />
            <span>View Leads in Portal</span>
            <ArrowRight size={13} className="text-purple-400" />
          </button>
        </div>
      )}

      {/* ── Directional Connection Handles (Left in from Luna Core / Right out) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-action"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Input from Luna Core Hub"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-action"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Output: Chained Action Pipeline"
      />

      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(168,85,247,0.8)]"
      />

      <div className="bg-gradient-to-r from-purple-950/80 via-indigo-950/60 to-slate-950/80 px-4 py-3 border-b border-purple-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-purple-500/20 border border-purple-500/40 text-purple-300 shadow-[0_0_12px_rgba(168,85,247,0.3)]">
            <Zap size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Action & Integration</h4>
            <p className="text-[10px] text-purple-300/70 font-mono">External Tool Trigger & Admin Hub</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
            ACTION
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-purple-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-purple-400" />
                <span className="font-bold text-white text-xs">
                  {nodeData.actionType?.toUpperCase() || 'EXTERNAL ACTION'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-purple-500/20 hover:bg-purple-500/35 text-purple-200 border border-purple-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-purple-300 font-bold">
                🎯 {nodeData.targetDestination || 'Webhook / Trigger'}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-purple-950/70 border border-purple-500/30 text-[10px] font-mono text-purple-300">
                {nodeData.autoExecute ? 'Autonomous' : 'Manual Trigger'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3.5 text-xs">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
            <Zap size={12} className="text-purple-400" /> Tool / Service Trigger
          </label>
          <select
            className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400/30 transition-all cursor-pointer font-medium"
            value={nodeData.actionType || 'whatsapp_lead'}
            onChange={(e) => handleChange('actionType', e.target.value)}
          >
            <option value="whatsapp_lead" className="bg-slate-900 text-slate-100">Capture & Send WhatsApp Lead (Owner Alert & RBAC Admin)</option>
            <option value="quick_sale" className="bg-slate-900 text-slate-100">Execute POS Sale & Print Thermal Receipt</option>
            <option value="stock_check" className="bg-slate-900 text-slate-100">Real-Time Inventory / Barcode Lookup</option>
            <option value="hotel_checkin" className="bg-slate-900 text-slate-100">Voice Hotel Check-in / Room Status</option>
            <option value="webhook" className="bg-slate-900 text-slate-100">Custom Webhook / Zapier HTTP POST</option>
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Send size={12} className="text-purple-400" /> Destination / Owner Phone / Endpoint
            </label>
            {isPhoneDestination && (
              <span className="text-[9px] font-mono text-amber-300 font-bold bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/30 flex items-center gap-1">
                <Crown size={10} className="text-amber-400" /> Admin Access Phone
              </span>
            )}
          </div>
          <input
            type="text"
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400/30 transition-all placeholder:text-slate-600 font-mono"
            placeholder={
              nodeData.actionType === 'webhook'
                ? 'https://api.yourdomain.com/webhook'
                : nodeData.actionType === 'whatsapp_lead'
                ? '+94 77 123 4567'
                : 'Default System Pipeline'
            }
            value={nodeData.targetDestination || ''}
            onChange={(e) => handleChange('targetDestination', e.target.value)}
          />
        </div>

        {/* 👑 Store Owner & Admin Autonomous WhatsApp Permissions Card */}
        {isPhoneDestination && (
          <div className="rounded-xl bg-gradient-to-br from-purple-950/40 via-slate-900/60 to-indigo-950/40 border border-purple-500/30 p-2.5 space-y-2 shadow-inner">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-purple-300">
                <Crown size={12} className="text-amber-400 animate-pulse" />
                Verified Store Owner & Admin Privileges
              </span>
              <span className="text-[8.5px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold flex items-center gap-1">
                <ShieldCheck size={10} /> ZERO-TRUST RBAC
              </span>
            </div>

            <p className="text-[10px] text-slate-300 leading-relaxed font-sans">
              The phone number added above is granted <strong className="text-purple-200 font-semibold">Store Administrator & Owner Privileges</strong>. When messaging Luna AI from this number over WhatsApp:
            </p>

            <div className="grid grid-cols-2 gap-1.5 text-[9px] font-mono text-slate-300">
              <div className="flex items-center gap-1.5 bg-purple-900/20 px-2 py-1 rounded border border-purple-500/20">
                <Sparkles size={11} className="text-purple-400 shrink-0" />
                <span>Price & Stock Changes</span>
              </div>
              <div className="flex items-center gap-1.5 bg-indigo-900/20 px-2 py-1 rounded border border-indigo-500/20">
                <Camera size={11} className="text-indigo-400 shrink-0" />
                <span>Photo / Bill Vision OCR</span>
              </div>
              <div className="flex items-center gap-1.5 bg-emerald-900/20 px-2 py-1 rounded border border-emerald-500/20">
                <BookOpen size={11} className="text-emerald-400 shrink-0" />
                <span>Knowledge Base Sync</span>
              </div>
              <div className="flex items-center gap-1.5 bg-rose-900/20 px-2 py-1 rounded border border-rose-500/20">
                <Bell size={11} className="text-rose-400 shrink-0" />
                <span>Real-Time Lead Alerts</span>
              </div>
            </div>

            <div className="text-[9px] text-slate-400 pt-0.5 border-t border-white/5 flex items-center justify-between">
              <span>Security Shield:</span>
              <span className="text-emerald-400 font-mono">Unauthorized callers are strictly denied</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-1 border-t border-white/5">
          <span className="text-[10px] text-slate-400 font-medium">Execution Mode:</span>
          <span className="text-[10px] font-mono text-purple-300 bg-purple-950/60 px-2 py-0.5 rounded border border-purple-500/20">
            Automated Tool Call
          </span>
        </div>

        {/* Quick Portal Data Link */}
        {openPortalData && (
          <div className="pt-2.5 border-t border-purple-500/20 flex items-center justify-between">
            <span className="text-[10px] text-purple-300/80 font-mono flex items-center gap-1">
              <Users size={11} className="text-purple-400" /> Leads Hub:
            </span>
            <button
              type="button"
              onClick={() => openPortalData('actionNode', id)}
              className="nodrag flex items-center gap-1 text-[10px] font-bold text-purple-200 hover:text-white bg-purple-500/20 hover:bg-purple-500/30 px-2.5 py-1 rounded-lg border border-purple-500/30 hover:border-purple-400 transition cursor-pointer"
            >
              <span>Manage Leads</span>
              <ArrowRight size={10} />
            </button>
          </div>
        )}

      </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

ActionNode.displayName = 'ActionNode';
export default ActionNode;
